import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton,
  Backdrop,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  NoDataContainer,
} from "../Common/styles/table";
import StatsCards from "../Dashboard/Cards/StatsCards";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import Loader from "../Common/assets/Loader";
import { AxiosResponse } from "axios";
import StatusBadge from "../Common/UI/StatusTag";
import { DateTime } from "luxon";
import ViewNamesModal from "../Appointments/ViewNamesModal";
import ParticipantsCell from "../Appointments/ParticipantsCell";
import { mapInviteesToParticipants } from "../Appointments/appointmentParticipantUtils";
import { AppointmentParticipant } from "../Appointments/appointmentParticipantTypes";
import ViewAppointmentModal from "./ViewAppointmentModal";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../Redux/hooks";
import { permissions } from "../../utils/roles";
import RescheduleAppointmentModal from "./RescheduleAppointmentModal";
import { appointmentStatusMap } from "../../const/appointment";

const getAppointmentCardConfig = (dateRange: string) => {
  const dateRangeLabel =
    dateRange === "today"
      ? ""
      : dateRange === "week"
      ? " This Week"
      : " This Month";

  return [
    {
      key: "totalScheduled",
      title: "Scheduled",
      desc: `Total Appointments Scheduled${dateRangeLabel}`,
      color: "#3F83F8",
    },
    {
      key: "totalCompleted",
      title: "Completed",
      desc: `Total Completed Appointments${dateRangeLabel}`,
      color: "#0E9F6E",
    },
    {
      key: "totalCanceled",
      title: "Cancelled",
      desc: `Total Cancelled Appointments${dateRangeLabel}`,
      color: "#F98080",
    },
    {
      key: "totalRescheduled",
      title: "Rescheduled",
      desc: `Total Rescheduled Appointments${dateRangeLabel}`,
      color: "#FFCA54",
    },
  ];
};

// Helper function to calculate date range
const getDateRange = (range: string) => {
  const now = DateTime.now();
  let startDate: DateTime;
  let endDate: DateTime;

  switch (range) {
    case "today":
      startDate = now.startOf("day");
      endDate = now.endOf("day");
      break;
    case "week":
      startDate = now.startOf("week"); // Monday
      endDate = now.endOf("week");
      break;
    case "month":
    default:
      startDate = now.startOf("month");
      endDate = now.endOf("month");
      break;
  }

  return {
    startDate: startDate,
    endDate: endDate,
  };
};

const StudyAppointmentsDashboard = () => {
  const studyPermissions = useAppSelector(
    (state) => state.study.studyPermissions
  );
  const [canEdit] = useState(
    studyPermissions.includes(permissions.manageAppointment)
  );
  const { id: studyId } = useParams();
  const [dateRange, setDateRange] = useState<string>("month");
  const [loading, setLoading] = useState(true);

  const [dashboardCards, setDashboardCards] = useState<any[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<any[]>([]);
  const [toggleSlots, setToggleSlots] = useState(false);

  // Modal state for viewing all participants/organizers
  const [viewModal, setViewModal] = useState<{
    open: boolean;
    type: "participants" | "organizers" | null;
    names: string[];
    participants: AppointmentParticipant[];
  }>({
    open: false,
    type: null,
    names: [],
    participants: [],
  });

  // Modal state for viewing appointment details
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showRescheduleModal, setRescheduleModal] = useState(false);
  const [rescheduleSlot, setRescheduleSlot] = useState<any>(null);
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Calculate date range
        const { startDate, endDate } = getDateRange(dateRange);

        // Build query params
        const params = new URLSearchParams({
          startDate: startDate.toUTC().toISO(),
          endDate: endDate.toUTC().toISO(),
        });

        // Fetch metrics data
        const metricsRes: AxiosResponse = await http.get(
          `/admin/study/${studyId}/appointments/metrics?${params.toString()}`
        );

        const metricsData = metricsRes.data?.data;

        // Fetch upcoming appointments
        const upcomingRes: AxiosResponse = await http.get(
          `/admin/study/${studyId}/appointments/upcoming?${params.toString()}`
        );

        const upcomingData = upcomingRes.data?.data;

        // Map card data with dynamic descriptions based on date range
        const cardData = getAppointmentCardConfig(dateRange).map((card) => ({
          ...card,
          value: metricsData?.[card.key] || 0,
        }));
        setDashboardCards(cardData);

        // Map upcoming appointments
        const appointments = upcomingData?.map((item: any) => {
          const scheduledFromDT = item?.scheduledFrom
            ? DateTime.fromISO(item?.scheduledFrom)
            : null;
          const scheduledToDT = item?.scheduledTo
            ? DateTime.fromISO(item?.scheduledTo)
            : null;

          const date = scheduledFromDT
            ? scheduledFromDT.toFormat("MM/dd/yyyy")
            : "";

          const timeRange =
            scheduledFromDT && scheduledToDT
              ? `${scheduledFromDT.toFormat(
                  "hh:mm a"
                )} - ${scheduledToDT.toFormat("hh:mm a")}`
              : "";

          const participants = mapInviteesToParticipants(item?.invitees);

          // Extract inviters names as array
          const invitersArray =
            item?.inviters
              ?.map((inv: any) => {
                const firstName = inv?.inviter?.firstName || "";
                const lastName = inv?.inviter?.lastName || "";
                return `${firstName} ${lastName}`.trim() || "Name Unknown";
              })
              .filter((name: string) => name) || [];

          // Extract unique cohort names based on unique cohortIds
          const cohortMap = new Map<string, string>();
          item?.invitees?.forEach((inv: any) => {
            const cohortId = inv?.invitee?.cohortId;
            const cohortName = inv?.invitee?.cohortName;
            if (cohortId && cohortName && !cohortMap.has(cohortId)) {
              cohortMap.set(cohortId, cohortName);
            }
          });
          const uniqueCohortNames = Array.from(cohortMap.values());

          return {
            id: item?.id,
            referenceId: item?.referenceId,
            date: date,
            time: timeRange,
            participants,
            organizersArray: invitersArray,
            cohortNames: uniqueCohortNames,
            notes: item?.notes || "",
            status: item?.status || "scheduled",
            room: `${item?.room?.site?.name || ""} - ${item?.room?.name || ""}`,
          };
        });
        setUpcomingAppointments(appointments || []);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [studyId, dateRange, toggleSlots]);

  const handleDateRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDateRange: string | null
  ) => {
    if (newDateRange !== null) {
      setDateRange(newDateRange);
    }
  };

  const handleViewMoreParticipants = (
    participants: AppointmentParticipant[],
  ) => {
    setViewModal({
      open: true,
      type: "participants",
      names: participants.map((p) => p.name),
      participants,
    });
  };

  const handleViewMoreOrganizers = (names: string[]) => {
    setViewModal({
      open: true,
      type: "organizers",
      names,
      participants: [],
    });
  };

  const handleCloseModal = () => {
    setViewModal({
      open: false,
      type: null,
      names: [],
      participants: [],
    });
  };

  const handleViewAppointment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  // Helper function to render names with "View More" option
  const renderNamesCell = (
    namesArray: string[],
    type: "participants" | "organizers"
  ) => {
    if (namesArray.length === 0) return "-";

    if (namesArray.length <= 2) {
      return namesArray.join(", ");
    }

    const firstTwo = namesArray.slice(0, 2).join(", ");
    const remaining = namesArray.length - 2;

    return (
      <Box>
        {firstTwo}
        <Typography
          component="span"
          sx={{
            ml: 1,
            color: "primary.main",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "12px",
            textDecoration: "underline",
            "&:hover": {
              color: "primary.dark",
            },
          }}
          onClick={() => handleViewMoreOrganizers(namesArray)}
        >
          +{remaining} more
        </Typography>
      </Box>
    );
  };

  const handleToggleSlots = () => {
    setToggleSlots((prev) => !prev);
  };

  const handleReschedule = (slot: any) => {
    setRescheduleSlot(slot);

    handleCloseAppointmentModal();
    setRescheduleModal(true);
  };

  return (
    <>
      {loading ? (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      ) : (
        <Box>
          {/* Filters Section */}
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              p: 2.5,
              borderRadius: "12px",
              mb: 3,
              boxShadow: "0px 1px 3px 0px #0000001A",
            }}
          >
            {/* <Typography
              fontSize={16}
              fontWeight={700}
              color="text.primary"
              mb={2}
            >
              Filters
            </Typography> */}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Date Range Filter */}
              <Box>
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="text.secondary"
                  mb={1}
                >
                  Date Range
                </Typography>
                <ToggleButtonGroup
                  value={dateRange}
                  exclusive
                  onChange={handleDateRangeChange}
                  aria-label="date range"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 2,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      border: "1px solid #D1D5DB",
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#FFFFFF",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="today">Today</ToggleButton>
                  <ToggleButton value="week">Week</ToggleButton>
                  <ToggleButton value="month">Month</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Box>

          {/* Dashboard Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gridGap: "16px",
              mb: 3,
            }}
          >
            {dashboardCards.map((card) => (
              <StatsCards key={card.key} data={card} />
            ))}
          </Box>

          {/* Upcoming Appointments Table */}
          <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
            <Box sx={{ mb: 2 }}>
              <Typography fontSize={18} fontWeight={700} color="text.primary">
                Upcoming Appointments
              </Typography>
              <Typography fontSize={12} fontWeight={500} color="text.secondary">
                Next 10 scheduled appointments
              </Typography>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Table sx={TableBorderRadiusTopLeftRight}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>Time</StyledTableCell>
                    <StyledTableCell>Participants</StyledTableCell>
                    <StyledTableCell>User Group</StyledTableCell>
                    <StyledTableCell>Internal Team</StyledTableCell>
                    <StyledTableCell>Room</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                {upcomingAppointments.length > 0 && (
                  <TableBody>
                    {upcomingAppointments.map((appointment) => (
                      <TableRow key={appointment.id}>
                        <StyledTableCell
                          onClick={() => handleViewAppointment(appointment)}
                          sx={{
                            cursor: "pointer",
                          }}
                        >
                          {appointment.date || "-"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {appointment.time || "-"}
                        </StyledTableCell>
                        <StyledTableCell>
                          <ParticipantsCell
                            participants={appointment.participants}
                            onViewMore={handleViewMoreParticipants}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {appointment.cohortNames &&
                          appointment.cohortNames.length > 0
                            ? appointment.cohortNames.join(", ")
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {renderNamesCell(
                            appointment.organizersArray,
                            "organizers"
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {appointment.room || "-"}
                        </StyledTableCell>
                        <StyledTableCell>
                          <StatusBadge
                            variant={
                              appointmentStatusMap[appointment?.status]?.variant
                            }
                            label={
                              appointmentStatusMap[appointment?.status]
                                ?.label ?? appointment?.status
                            }
                          />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>

              {upcomingAppointments.length === 0 && (
                <NoDataContainer>
                  <Typography variant="body1" color="gray">
                    No upcoming appointments
                  </Typography>
                </NoDataContainer>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Modal for viewing all participants/organizers */}
      {viewModal.open && (
        <ViewNamesModal
          open={viewModal.open}
          onClose={handleCloseModal}
          type={viewModal.type}
          names={viewModal.names}
          participants={viewModal.participants}
        />
      )}

      {/* Modal for viewing appointment details */}
      {showAppointmentModal && selectedAppointment && (
        <ViewAppointmentModal
          showModal={showAppointmentModal}
          closeModal={handleCloseAppointmentModal}
          roomId={selectedAppointment?.id || ""}
          refreshPage={handleToggleSlots}
          slotId={selectedAppointment?.referenceId || ""}
          canEdit={canEdit}
          handleReschedule={handleReschedule}
        />
      )}

      {showRescheduleModal && rescheduleSlot && (
        <RescheduleAppointmentModal
          showModal={showRescheduleModal}
          closeModal={() => {
            setRescheduleModal(false);
            setRescheduleSlot(null);
          }}
          slotData={rescheduleSlot}
          roomId={rescheduleSlot?.roomId || ""}
          refreshPage={handleToggleSlots}
        />
      )}
    </>
  );
};

export default StudyAppointmentsDashboard;
